import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface SlideInfo {
  id: string;
  file: string;
  title: string;
  position?: number;
  thumbnail?: string;
  // Per-slide overrides (optional — falls back to global config)
  interval?: number;
  transition?: TransitionType;
  transitionDuration?: number;
  // Speaker notes — free-form text shown in PresenterDashboard's notes
  // pane. Edited via the notes textarea in slides-panel; saved per-slide
  // in slides.json with a 500ms debounce.
  speakerNotes?: string;
}

export type TransitionType =
  | "fade"
  | "dissolve"
  | "slide"
  | "slide-up"
  | "slide-down"
  | "zoom"
  | "wipe"
  | "wipe-up"
  | "wipe-down"
  | "wipe-tl"
  | "wipe-tr"
  | "wipe-bl"
  | "wipe-br"
  | "flip"
  | "cube"
  | "none";

export type AspectRatio = "16:9" | "4:3";

export interface PresentationConfig {
  autoplay: boolean;
  interval: number;
  loop: boolean;
  transition: TransitionType;
  transitionDuration: number;
  showControls: boolean;
  pauseOnInteraction: boolean;
  resumeDelay: number;
  aspectRatio: AspectRatio;
}

const DEFAULT_PRESENTATION_CONFIG: PresentationConfig = {
  autoplay: false,
  interval: 5000,
  loop: false,
  transition: "fade",
  transitionDuration: 300,
  showControls: true,
  pauseOnInteraction: true,
  resumeDelay: 10000,
  aspectRatio: "16:9",
};

interface SlideState {
  slides: SlideInfo[];
  currentSlideIndex: number;
  totalSlides: number;
  isSlideProject: boolean;
  isSidebarCollapsed: boolean;
  isExporting: boolean;
  exportProgress: number;
  presentationConfig: PresentationConfig;
  // Bumped after a thumbnail-capture event so the per-slide thumbnail hook
  // re-fetches even though the URL (derived from slide.id) is stable.
  thumbnailRevision: number;
  // Single-slot promise chain: every slide-manifest mutation (reorder /
  // duplicate / delete) appends itself here so the sidecar sees them in
  // submit order. Without this, a duplicate POST can beat a reorder PUT
  // to disk and clobber the new ordering.
  pendingMutation: Promise<void> | null;

  setSlides: (slides: SlideInfo[]) => void;
  setPresentationConfig: (config: Partial<PresentationConfig>) => void;
  updateSlideConfig: (slideId: string, config: { interval?: number | null; transition?: TransitionType | null; transitionDuration?: number | null; speakerNotes?: string | null }) => void;
  deleteSlide: (slideId: string) => void;
  setCurrentSlide: (index: number) => void;
  setTotalSlides: (total: number) => void;
  setIsSlideProject: (value: boolean) => void;
  setSidebarCollapsed: (value: boolean) => void;
  setIsExporting: (value: boolean) => void;
  setExportProgress: (value: number) => void;
  bumpThumbnailRevision: () => void;
  runMutation: <T>(fn: () => Promise<T>) => Promise<T>;
  reset: () => void;
}

export const useSlideStore = create<SlideState>()(
  devtools(
    (set, get) => ({
      slides: [],
      currentSlideIndex: 0,
      totalSlides: 0,
      isSlideProject: false,
      isSidebarCollapsed: false,
      isExporting: false,
      exportProgress: 0,
      presentationConfig: { ...DEFAULT_PRESENTATION_CONFIG },
      thumbnailRevision: 0,
      pendingMutation: null,

      setSlides: (slides) => set({ slides, totalSlides: slides.length }),
      setPresentationConfig: (config) =>
        set((s) => ({
          presentationConfig: { ...s.presentationConfig, ...config },
        })),
      updateSlideConfig: (slideId, config) =>
        set((s) => ({
          slides: s.slides.map((slide) => {
            if (slide.id !== slideId) return slide;
            const updated = { ...slide };
            // null = clear override, undefined = no change, value = set override
            for (const [key, val] of Object.entries(config)) {
              if (val === null) {
                delete updated[key as keyof typeof updated];
              } else if (val !== undefined) {
                (updated as Record<string, unknown>)[key] = val;
              }
            }
            return updated;
          }),
        })),
      // Optimistic local delete — server confirmation happens in the panel
      // handler. currentSlideIndex moves to preserve the user's focus:
      //   idx <  current → decrement (visible slides shifted left under it)
      //   idx === current → clamp to last index (current slide vanished)
      //   idx >  current → no change (deleted slide was below)
      deleteSlide: (slideId) =>
        set((s) => {
          const idx = s.slides.findIndex((sl) => sl.id === slideId);
          if (idx === -1) return s;
          const next = s.slides.filter((sl) => sl.id !== slideId);
          let nextCurrent = s.currentSlideIndex;
          if (idx < s.currentSlideIndex) {
            nextCurrent = s.currentSlideIndex - 1;
          } else if (idx === s.currentSlideIndex) {
            nextCurrent = Math.min(s.currentSlideIndex, next.length - 1);
          }
          return {
            slides: next,
            totalSlides: next.length,
            currentSlideIndex: Math.max(0, nextCurrent),
          };
        }),
      setCurrentSlide: (index) => set({ currentSlideIndex: index }),
      setTotalSlides: (total) => set({ totalSlides: total }),
      setIsSlideProject: (value) => set({ isSlideProject: value }),
      setSidebarCollapsed: (value) => set({ isSidebarCollapsed: value }),
      setIsExporting: (value) => set({ isExporting: value }),
      setExportProgress: (value) => set({ exportProgress: value }),
      bumpThumbnailRevision: () =>
        set((s) => ({ thumbnailRevision: s.thumbnailRevision + 1 })),
      // Serialize manifest mutations so the sidecar sees them in submit order.
      // The previous mutation's promise becomes the prefix for the next one;
      // we never block on a previous failure so one rejected call doesn't
      // stall the queue.
      runMutation: async <T>(fn: () => Promise<T>): Promise<T> => {
        const prev = get().pendingMutation ?? Promise.resolve();
        let resolveSlot: () => void = () => undefined;
        const slot = new Promise<void>((r) => {
          resolveSlot = r;
        });
        set({ pendingMutation: slot });
        try {
          await prev.catch(() => undefined);
          return await fn();
        } finally {
          resolveSlot();
          if (get().pendingMutation === slot) {
            set({ pendingMutation: null });
          }
        }
      },
      reset: () =>
        set({
          slides: [],
          currentSlideIndex: 0,
          totalSlides: 0,
          isSlideProject: false,
          isSidebarCollapsed: false,
          isExporting: false,
          exportProgress: 0,
          presentationConfig: { ...DEFAULT_PRESENTATION_CONFIG },
          thumbnailRevision: 0,
          pendingMutation: null,
        }),
    }),
    { name: "slide-store" },
  ),
);
